import { useState, useEffect, useRef, useCallback } from 'react'
import { transcriptsService } from '../services/transcriptsService'
import { containsDevanagari } from '../../../utils/formatters'
import type {
  LiveTranscriptPreview,
  Transcript,
  TranscriptStreamEvent,
  TranscriptPreviewEvent,
  TranscriptInterimEvent,
} from '../../../types/transcript.types'

export type TranscriptStreamStatus =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'error'

function todayLocalISO(): string {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
}

export function useTranscripts(
  day?: string,
  userId?: number,
  propertyIdOverride?: number,
): {
  transcripts: Transcript[]
  livePreviews: Map<string, LiveTranscriptPreview>
  loading: boolean
  error: string | null
  streamStatus: TranscriptStreamStatus
  refetch: () => void
} {
  // No fallback to the user's first property. Showing one the user did not pick
  // — without naming it anywhere on the page — is exactly the guessing the
  // property store stopped doing.
  const propertyId = propertyIdOverride

  const [transcripts, setTranscripts] = useState<Transcript[]>([])
  const [livePreviews, setLivePreviews] = useState<Map<string, LiveTranscriptPreview>>(
    () => new Map(),
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [streamStatus, setStreamStatus] = useState<TranscriptStreamStatus>('idle')

  const seenIdsRef = useRef<Set<number>>(new Set())
  const hasConnectedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    let close: (() => void) | null = null
    seenIdsRef.current = new Set()
    hasConnectedRef.current = false

    const isLiveMode = (!day || day === todayLocalISO()) && propertyId !== undefined

    // Reflected before the fetch, not after, so a past day cannot sit under a
    // stale "Live" badge while its history loads.
    setStreamStatus(isLiveMode ? 'connecting' : 'idle')
    if (!isLiveMode) setLivePreviews(new Map())

    async function loadThenStream(): Promise<void> {
      setLoading(true)
      try {
        const data = await transcriptsService.getAll(day, userId, propertyId)
        if (cancelled) return
        data.forEach((t) => seenIdsRef.current.add(t.id))
        setTranscripts(data)
        setError(null)
      } catch (err) {
        if (cancelled) return
        setTranscripts([])
        setError(err instanceof Error ? err.message : 'Failed to fetch transcripts')
      } finally {
        if (!cancelled) setLoading(false)
      }

      // The stream opens only once the read has landed. Firing both together
      // let a slow fetch resolve on top of live events and drop them for good:
      // the streamed row is replaced by the older snapshot, and because its id
      // is already in seenIdsRef the reconnect refetch skips it too.
      if (cancelled || !isLiveMode || propertyId === undefined) return

      close = transcriptsService.streamLive(propertyId, {
        onPreview: (t: TranscriptPreviewEvent) => {
          if (cancelled) return
          const key = t.device_session
          setLivePreviews((prev) => {
            const next = new Map(prev)
            next.set(key, {
              device_session: key,
              device_id: t.device_id ?? null,
              transcript_text: t.transcript_text,
              device_user_id: t.device_user_id ?? null,
              username: t.username ?? null,
              mac: t.mac ?? null,
              confidence: t.confidence ?? null,
              isUrdu: containsDevanagari(t.transcript_text),
            })
            return next
          })
        },
        onInterim: (t: TranscriptInterimEvent) => {
          if (cancelled) return
          const key = t.device_session
          setLivePreviews((prev) => {
            const next = new Map(prev)
            next.set(key, {
              device_session: key,
              device_id: t.device_id ?? null,
              transcript_text: t.transcript_text,
              device_user_id: t.device_user_id ?? null,
              username: t.username ?? null,
              mac: t.mac ?? null,
              confidence: t.confidence ?? null,
              isUrdu: containsDevanagari(t.transcript_text),
            })
            return next
          })
        },
        onTranscript: (t: TranscriptStreamEvent) => {
          if (cancelled) return
          if (seenIdsRef.current.has(t.id)) return
          seenIdsRef.current.add(t.id)

          if (t.device_session) {
            setLivePreviews((prev) => {
              if (!prev.has(t.device_session!)) return prev
              const next = new Map(prev)
              next.delete(t.device_session!)
              return next
            })
          }

          const row: Transcript = {
            id: t.id,
            property_id: t.property_id,
            device_id: t.device_id ?? 0,
            device_user_id: t.device_user_id,
            transcript_text: t.transcript_text,
            intent_type: t.intent_type,
            intent_data: t.intent_data,
            started_at: t.started_at,
            created_at: t.created_at,
            username: t.username ?? null,
            audio_url: t.audio_url ?? null,
            confidence: t.confidence ?? null,
            audio_duration: t.audio_duration ?? null,
          }
          setTranscripts((prev) => [row, ...prev])
        },
        onOpen: () => {
          if (cancelled) return
          const wasReconnect = hasConnectedRef.current
          hasConnectedRef.current = true
          setStreamStatus('live')
          setError(null)
          if (!wasReconnect) return
          transcriptsService
            .getAll(day, userId, propertyId)
            .then((data) => {
              if (cancelled) return
              const additions = data.filter((t) => !seenIdsRef.current.has(t.id))
              if (additions.length > 0) {
                additions.forEach((t) => seenIdsRef.current.add(t.id))
                setTranscripts((prev) => [...additions, ...prev])
              }
              setLivePreviews(new Map())
            })
            .catch(() => {})
        },
        onError: ({ status, fatal }) => {
          if (cancelled) return
          if (!fatal) { setStreamStatus('reconnecting'); return }
          setStreamStatus('error')
          if (status === 401) setError('Your session expired — please sign in again.')
          else if (status === 403) setError('You no longer have access to this property.')
          else if (status === 429) setError('Too many live transcript streams open. Close another tab and refresh.')
        },
        onAuthRevoked: () => {
          if (cancelled) return
          setStreamStatus('error')
          setError('Your access to this property was removed.')
        },
      })
    }

    loadThenStream()

    return () => {
      cancelled = true
      setLivePreviews(new Map())
      close?.()
    }
  }, [day, userId, propertyId])

  const refetch = useCallback(() => {
    setLoading(true)
    transcriptsService
      .getAll(day, userId, propertyId)
      .then((data) => {
        seenIdsRef.current = new Set(data.map((t) => t.id))
        setTranscripts(data)
        setError(null)
      })
      .catch((err) => {
        setTranscripts([])
        setError(err instanceof Error ? err.message : 'Failed to fetch transcripts')
      })
      .finally(() => setLoading(false))
  }, [day, userId, propertyId])

  return { transcripts, livePreviews, loading, error, streamStatus, refetch }
}
